import { Controller } from "@hotwired/stimulus"

// Per-slot presenter clip source toggle + upload (Presenter Step Redesign P4).
// Library | Upload: the inactive <fieldset> is natively `disabled`, so only the
// active source's clip_asset_id submits — robust even before/without JS.
// Uploading POSTs the file to the endpoint and writes the returned org-scoped
// asset id into the hidden field for the wizard form to save.
export default class extends Controller {
  static targets = [
    "sourceRadio", "librarySection", "uploadSection",
    "fileInput", "clipIdField", "transparentCheckbox", "status", "thumbnail",
    "generateCharacter", "generateLine", "generateStatus"
  ]
  static values = { url: String, generateUrl: String, affinity: String }

  connect() {
    this.toggle()
  }

  currentSource() {
    const checked = this.sourceRadioTargets.find((radio) => radio.checked)
    return checked ? checked.value : "library"
  }

  toggle() {
    const upload = this.currentSource() === "upload"
    if (this.hasLibrarySectionTarget) {
      this.librarySectionTarget.disabled = upload
      this.librarySectionTarget.hidden = upload
    }
    if (this.hasUploadSectionTarget) {
      this.uploadSectionTarget.disabled = !upload
      this.uploadSectionTarget.hidden = !upload
    }
  }

  async upload() {
    const file = this.fileInputTarget.files && this.fileInputTarget.files[0]
    if (!file) return

    this.setStatus("Uploading…")
    const body = new FormData()
    body.append("file", file)
    if (this.hasTransparentCheckboxTarget && this.transparentCheckboxTarget.checked) {
      body.append("declared_transparent", "1")
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" }
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        this.setStatus(data.error || "Upload failed.")
        return
      }

      this.clipIdFieldTarget.value = data.id
      const look = data.render_mode === "pip" ? "a framed card" : "a full figure"
      this.setStatus(`Uploaded “${data.label}” — shows as ${look}.`)
      if (data.thumbnail_url && this.hasThumbnailTarget) {
        this.thumbnailTarget.src = data.thumbnail_url
        this.thumbnailTarget.hidden = false
      }
    } catch (error) {
      this.setStatus("Upload failed.")
    }
  }

  // P5: enqueue an on-demand presenter-line burn. The new clip lands in the
  // library picker on a later visit (~3-5 min) — not selected in place.
  async generate() {
    const characterId = this.hasGenerateCharacterTarget ? this.generateCharacterTarget.value : ""
    const line = this.hasGenerateLineTarget ? this.generateLineTarget.value.trim() : ""
    if (!characterId || !line) {
      this.setGenerateStatus("Pick a presenter and type a line.")
      return
    }

    this.setGenerateStatus("Queuing…")
    const body = new FormData()
    body.append("character_id", characterId)
    body.append("phrase_text", line)
    body.append("placement_affinity", this.affinityValue)

    try {
      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" }
      })
      const data = await response.json().catch(() => ({}))
      this.setGenerateStatus(response.ok ? (data.message || "Queued.") : (data.error || "Couldn't queue generation."))
    } catch (error) {
      this.setGenerateStatus("Couldn't queue generation.")
    }
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  setGenerateStatus(text) {
    if (this.hasGenerateStatusTarget) this.generateStatusTarget.textContent = text
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
